import { makeStyles } from '@material-ui/styles'
import ExternalLink from 'core/components/ExternalLink'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import React, { useState } from 'react'
import { useControlPlaneServicesCheck } from './ControlPlaneServicesContext'
import { getHealthStatus } from './helper'
import TroubleShootModal from './TroubleShootModal'
import { ServiceNameMap } from './useControlPlaneServicesHealth'
import DataKeys from 'k8s/DataKeys'
import { AcceptCertificate } from './HealthStatusBadge'
import { ServiceStatus } from './selector'

const useColumnStyles = makeStyles((theme: Theme) => ({
  serviceHealthBadge: {
    cursor: 'pointer',
  },
}))

interface HealthStatusBadgeProps {
  value: any
  serviceType: string
  endpoint?: string
}

export const ErrorMessageComponent = ({ value, item }: { value: string; item: any }) => {
  return (
    <Text variant="body2">
      {item?.status === ServiceStatus.CertificateError ? (
        <AcceptCertificate endpoint={item?.url} />
      ) : (
        value
      )}
    </Text>
  )
}

export const HealthStatusBadge: React.FC<HealthStatusBadgeProps> = ({
  value,
  serviceType,
  endpoint,
}) => {
  const classes = useColumnStyles()
  const { label, variant } = getHealthStatus(value)
  const serviceName = ServiceNameMap[serviceType] || serviceType
  const [troubleshootOptions, setTroubleshootOptions] = useState<boolean>(false)

  return (
    <>
      <Badge
        className={variant === 'warning' ? classes.serviceHealthBadge : ''}
        variant={variant}
        text={label}
        onClick={(e) => {
          if (value === ServiceStatus.NetworkError) {
            setTroubleshootOptions(true)
            e.stopPropagation()
            e.preventDefault()
          }
        }}
      />
      {value === ServiceStatus.NetworkError && (
        <TroubleShootModal
          open={!!troubleshootOptions}
          onClose={() => setTroubleshootOptions(false)}
          serviceName={serviceName}
          linkUrl={''}
          title="Troubleshooting Steps"
          maxWidth={800}
          steps={[
            {
              id: 1,
              text: `Make sure that the ${serviceName} service is accessible from the network you are on.`,
            },
            {
              id: 2,
              text: <AcceptCertificate endpoint={endpoint} />,
            },
          ]}
        />
      )}
    </>
  )
}

const columns = [
  {
    key: 'displayName',
    label: 'Services',
    CellComponent: ({ value, item }) => <Text variant="caption1">{value || item?.type}</Text>,
  },

  {
    key: 'status',
    label: 'Status',
    CellComponent: ({ value, item }) => (
      <HealthStatusBadge value={value} serviceType={item?.type} endpoint={item?.url} />
    ),
  },
  {
    key: 'error',
    label: 'Error Details',
    CellComponent: ErrorMessageComponent,
  },
  {
    key: 'url',
    label: 'URL',
    display: false,
    CellComponent: ({ value }) =>
      value?.split(',').map((url, idx) => (
        <React.Fragment key={`url-${idx}`}>
          <ExternalLink url={url.trim()}>{url.trim()}</ExternalLink>
          <br />
        </React.Fragment>
      )),
  },
]

export default function ApiServicesPage() {
  const { servicesList, isLoading, reload, lastUpdated } = useControlPlaneServicesCheck()

  return (
    <ListContainer<any, any>
      dataKey={DataKeys.ControlPlaneServicesHealth}
      uniqueIdentifier={'name'}
      label="API Endpoints Health"
      searchTargets={['name', 'clusterName']}
      loading={isLoading}
      loadingMessage={'Checking API endpoints health...'}
      onRefresh={() => reload(true, true)}
      data={servicesList}
      columns={columns}
      emptyContent="No services found"
      hideCheckboxes
      showBreadcrumbs={false}
    />
  )
}
